import { useSubpantalla } from '../ui/navegacionReact';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Clock, File, FileText, Folder, FolderLock, FolderPlus, Grid3x3, Image, LayoutList, Lock, Search, Share2, Star, Trash2, Upload, Users,
} from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import Ayuda from '../ui/Ayuda';
import { Cargando, ErrorConReintento, EstadoVacio, SinCoincidencias } from '../ui/EstadoDePantalla';
import Ficha from './Ficha';
import Subir from './Subir';
import Motivo from '../experiencia/Motivo';
import { CLASIFICACION, cuando, ordenar, porQueNo, tamano } from './formato';

const VISTAS = [
  { id: 'archivos', etiqueta: 'Archivos', icono: Folder },
  { id: 'compartido', etiqueta: 'Compartido conmigo', icono: Users },
  { id: 'recientes', etiqueta: 'Recientes', icono: Clock },
  { id: 'favoritos', etiqueta: 'Favoritos', icono: Star },
  { id: 'papelera', etiqueta: 'Papelera', icono: Trash2 },
];
const CLAVE_MODO = 'tego.archivos.modo';

function IconoDe({ tipo, className }) {
  if (['jpg', 'png', 'webp', 'heic'].includes(tipo)) return <Image className={className} aria-hidden="true" />;
  if (['pdf', 'docx', 'txt', 'csv', 'xlsx', 'pptx'].includes(tipo)) return <FileText className={className} aria-hidden="true" />;
  return <File className={className} aria-hidden="true" />;
}

function BarraDeUso({ cuota }) {
  if (!cuota) return null;
  const tono = cuota.lleno ? 'bg-red-600' : cuota.aviso ? 'bg-amber-500' : 'bg-[var(--color-marca)]';
  return (
    <div className="text-xs text-gray-700" data-cuota>
      <div className="flex justify-between"><span>{tamano(cuota.usados_bytes)} de {cuota.tope_gb} GB</span><span>{cuota.por_ciento} %</span></div>
      <div className="h-1.5 bg-gray-200 rounded mt-1" role="progressbar" aria-valuenow={cuota.por_ciento} aria-valuemin="0" aria-valuemax="100" aria-label="Espacio usado">
        <div className={`h-1.5 rounded ${tono}`} style={{ width: `${Math.min(100, cuota.por_ciento)}%` }} />
      </div>
      {cuota.aviso && !cuota.lleno && <p className="text-amber-800 mt-1">Queda poco espacio. Manda a la papelera lo que ya no sirva o pide más recursos.</p>}
      {cuota.lleno && <p className="text-red-700 mt-1">Sin espacio: no se pueden subir archivos hasta liberar o ampliar.</p>}
    </div>
  );
}

/**
 * Archivos · DIRECTRIZ-ARCHIVOS Parte B. Cinco vistas, cuadrícula o lista,
 * arrastrar y soltar, selección múltiple, atajos (N, U, /, Supr) y el
 * cajón lateral de cada archivo.
 */
export default function Archivos({ contexto }) {
  const { puede } = useAuth();
  const [vista, setVista] = useState('archivos');
  useSubpantalla('vista', vista !== 'archivos', () => setVista('archivos'), { valor: vista, abrir: setVista, reemplaza: true });
  const [idCarpeta, setIdCarpeta] = useState(contexto?.id_carpeta ?? null);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [modo, setModo] = useState(() => { try { return localStorage.getItem(CLAVE_MODO) || 'lista'; } catch { return 'lista'; } });
  const [busqueda, setBusqueda] = useState('');
  const [resultados, setResultados] = useState(null);
  const [seleccion, setSeleccion] = useState([]);
  const [fichaId, setFichaId] = useState(contexto?.id_archivo ?? null);
  useSubpantalla('id_archivo', fichaId != null, () => setFichaId(null), { valor: fichaId, abrir: setFichaId, enUrl: true, reemplaza: true });
  const [subiendo, setSubiendo] = useState(false);
  const [nuevaCarpeta, setNuevaCarpeta] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [arrastrando, setArrastrando] = useState(false);
  const buscador = useRef(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      let r;
      if (vista === 'archivos') r = await api.get(`/archivos${idCarpeta != null ? `?id_carpeta=${idCarpeta}` : ''}`);
      else if (vista === 'compartido') r = { ...(await api.get('/archivos/compartido-conmigo')), cuota: datos?.cuota };
      else if (vista === 'recientes') r = { carpetas: [], ...(await api.get('/archivos/recientes')), cuota: datos?.cuota };
      else if (vista === 'favoritos') r = { ...(await api.get('/archivos/favoritos')), cuota: datos?.cuota };
      else r = { ...(await api.get('/archivos/papelera')), cuota: datos?.cuota };
      if (!r.cuota) r.cuota = await api.get('/archivos/cuota').catch(() => null);
      setDatos(r); setError(null);
    } catch (e) { setError(e.message); } finally { setCargando(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vista, idCarpeta]);
  useEffect(() => { cargar(); setSeleccion([]); }, [cargar]);
  useEffect(() => { try { localStorage.setItem(CLAVE_MODO, modo); } catch { /* sin almacenamiento */ } }, [modo]);

  useEffect(() => {
    if (!busqueda.trim()) { setResultados(null); return undefined; }
    const t = setTimeout(async () => {
      try { setResultados(await api.get(`/archivos/buscar?q=${encodeURIComponent(busqueda.trim())}`)); } catch { setResultados({ archivos: [] }); }
    }, 250);
    return () => clearTimeout(t);
  }, [busqueda]);

  // Tres estados del permiso, no dos. Mientras la respuesta no ha llegado
  // —`datos` sigue en null, sea por carga o por un tropiezo de red— el
  // permiso es DESCONOCIDO, no negado: no se afirma «tu rol no sube» hasta
  // saberlo de verdad. Un permiso desconocido no es un permiso negado.
  const permisoDesconocido = !datos;
  const permisos = datos?.permisos || [];
  const soloLectura = Boolean(datos?.solo_lectura);
  const puedeSubir = !porQueNo({ permisos, accion: 'subir', soloLectura }) && vista === 'archivos';
  const razonNoSubir = permisoDesconocido ? null : porQueNo({ permisos, accion: 'subir', soloLectura });
  const razonNoCarpeta = permisoDesconocido ? null : porQueNo({ permisos, accion: 'nueva-carpeta', soloLectura });

  async function hacer(fn, exito) {
    setAviso(null);
    try { const r = await fn(); setAviso(exito || r?.dice || null); await cargar(); }
    catch (e) { setAviso({ error: e instanceof ErrorApi ? e.message : 'No se pudo hacer. Revisa la conexión y vuelve a intentarlo.' }); }
  }

  async function aPapelera(items) {
    for (const it of items) {
      await api.post(it.esCarpeta ? `/archivos/carpetas/${it.id}/papelera` : `/archivos/${it.id}/papelera`, {});
    }
    return { dice: `${items.length} elemento(s) en la papelera. Se pueden restaurar durante ${datos?.papelera?.dias || 30} días.` };
  }

  async function mover(items, destino) {
    for (const it of items) {
      await api.post(it.esCarpeta ? `/archivos/carpetas/${it.id}/mover` : `/archivos/${it.id}/mover`, { id_destino: destino });
    }
    return { dice: 'Movido.' };
  }

  useEffect(() => {
    function atajos(ev) {
      const enCampo = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName);
      if (ev.key === '/' && !enCampo) { ev.preventDefault(); buscador.current?.focus(); }
      if (enCampo) return;
      if (ev.key.toLowerCase() === 'n' && puedeSubir) { ev.preventDefault(); setNuevaCarpeta(''); }
      if (ev.key.toLowerCase() === 'u' && puedeSubir) { ev.preventDefault(); setSubiendo(true); }
      if (ev.key === 'Delete' && seleccion.length && !porQueNo({ permisos, accion: 'papelera', seleccion })) {
        ev.preventDefault(); hacer(() => aPapelera(seleccion));
      }
    }
    document.addEventListener('keydown', atajos);
    return () => document.removeEventListener('keydown', atajos);
  });

  const carpetas = useMemo(() => ordenar(datos?.carpetas || []), [datos]);
  const archivos = useMemo(() => (vista === 'recientes' ? datos?.archivos || [] : ordenar(datos?.archivos || [])), [datos, vista]);
  const migas = datos?.migas || [];

  function alternar(item, esCarpeta, ev) {
    const clave = { id: item.id, esCarpeta };
    if (ev?.shiftKey || ev?.ctrlKey || ev?.metaKey) {
      setSeleccion((s) => (s.some((x) => x.id === item.id && x.esCarpeta === esCarpeta) ? s.filter((x) => !(x.id === item.id && x.esCarpeta === esCarpeta)) : [...s, clave]));
      return;
    }
    if (esCarpeta) { if (vista === 'archivos' || vista === 'compartido' || vista === 'favoritos') { setVista('archivos'); setIdCarpeta(item.id); } }
    else setFichaId(item.id);
    setSeleccion([clave]);
  }

  function alSoltar(ev, destino) {
    ev.preventDefault(); setArrastrando(false);
    const archivosSueltos = ev.dataTransfer?.files;
    if (archivosSueltos && archivosSueltos.length) {
      if (!puedeSubir) { if (!permisoDesconocido) setAviso({ error: porQueNo({ permisos, accion: 'subir', soloLectura }) }); return; }
      setSubiendo(true);
      setTimeout(() => window.dispatchEvent(new CustomEvent('tego-archivos-soltados', { detail: { archivos: Array.from(archivosSueltos), idCarpeta: destino ?? idCarpeta } })), 0);
      return;
    }
    const crudo = ev.dataTransfer?.getData('application/x-tego-archivos');
    if (crudo && destino !== undefined) {
      const items = JSON.parse(crudo);
      if (items.some((i) => i.esCarpeta && i.id === destino)) return;
      hacer(() => mover(items, destino));
    }
  }

  function iniciarArrastre(ev, item, esCarpeta) {
    const items = seleccion.some((x) => x.id === item.id && x.esCarpeta === esCarpeta) ? seleccion : [{ id: item.id, esCarpeta }];
    ev.dataTransfer.setData('application/x-tego-archivos', JSON.stringify(items));
    ev.dataTransfer.effectAllowed = 'move';
  }

  const lista = resultados ? resultados.archivos : archivos;
  const hayFiltro = Boolean(busqueda.trim());
  const seleccionado = (item, esCarpeta) => seleccion.some((x) => x.id === item.id && x.esCarpeta === esCarpeta);

  return (
    <div className="space-y-3" onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }} onDragLeave={() => setArrastrando(false)} onDrop={(e) => alSoltar(e, undefined)}>
      <header>
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">Archivos <Ayuda id="archivos.pantalla" /></h1>
        <p className="text-sm text-gray-600">Los documentos y expedientes de tu empresa, en un solo lugar: sube, organiza, comparte y encuentra.</p>
      </header>

      <div className="flex flex-col md:flex-row gap-3">
        {/* Izquierda: las cinco vistas y la barra de uso */}
        <nav className="md:w-56 shrink-0 space-y-1" aria-label="Vistas de Archivos">
          <div className="flex md:flex-col gap-1 overflow-x-auto">
            {VISTAS.map((v) => (
              <button key={v.id} type="button" onClick={() => { setVista(v.id); setIdCarpeta(null); setBusqueda(''); }}
                      aria-current={vista === v.id ? 'page' : undefined}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap ${vista === v.id ? 'bg-[var(--color-marca-suave)] text-[var(--color-marca)] font-semibold' : 'text-gray-700 hover:bg-gray-100'}`}>
                <v.icono className="h-4 w-4" aria-hidden="true" /> {v.etiqueta}
              </button>
            ))}
          </div>
          <div className="pt-2 md:pt-4"><BarraDeUso cuota={datos?.cuota} /></div>
        </nav>

        {/* Centro */}
        <main className="flex-1 min-w-0 space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <label className="flex-1 min-w-[12rem] relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
              <input ref={buscador} value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar por nombre o por lo que dice el documento  ( / )"
                     aria-label="Buscar archivos" className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm" />
            </label>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden" role="group" aria-label="Cómo ver">
              <button type="button" onClick={() => setModo('lista')} aria-pressed={modo === 'lista'} className={`px-3 py-2 text-sm flex items-center gap-1 ${modo === 'lista' ? 'bg-gray-100 font-semibold' : ''}`}><LayoutList className="h-4 w-4" /> Lista</button>
              <button type="button" onClick={() => setModo('cuadricula')} aria-pressed={modo === 'cuadricula'} className={`px-3 py-2 text-sm flex items-center gap-1 ${modo === 'cuadricula' ? 'bg-gray-100 font-semibold' : ''}`}><Grid3x3 className="h-4 w-4" /> Cuadrícula</button>
            </div>
            <button type="button" onClick={() => setNuevaCarpeta('')} disabled={!puedeSubir} title={razonNoCarpeta || 'N'}
                    className="border border-gray-300 px-3 py-2 rounded-lg text-sm flex items-center gap-1 disabled:opacity-60"><FolderPlus className="h-4 w-4" /> Nueva carpeta</button>
            <button type="button" onClick={() => setSubiendo(true)} disabled={!puedeSubir} title={razonNoSubir || 'U'}
                    className="bg-[var(--color-marca)] text-white font-semibold px-3 py-2 rounded-lg text-sm flex items-center gap-1 disabled:opacity-60"><Upload className="h-4 w-4" /> Subir</button>
          </div>
          {!permisoDesconocido && !puedeSubir && vista === 'archivos' && <p className="text-xs text-gray-600">{razonNoSubir}</p>}

          {nuevaCarpeta !== null && (
            <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); const nombre = nuevaCarpeta.trim(); if (!nombre) return; setNuevaCarpeta(null); hacer(() => api.post('/archivos/carpetas', { nombre, id_padre: idCarpeta }), `Carpeta «${nombre}» creada.`); }}>
              {/* `required`: pulsar «Crear» con el nombre vacío no puede quedarse
                  mudo (auditoría de botones, 19 sep 2026); el navegador dice
                  «completa este campo» encima del campo. */}
              <input autoFocus required value={nuevaCarpeta} onChange={(e) => setNuevaCarpeta(e.target.value)} placeholder="Nombre de la carpeta" aria-label="Nombre de la carpeta nueva" className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              {/* P12 (29 sep 2026): el aviso nativo de `required` no lo ve nadie
                  que no tenga el ratón encima, y el censo lo contaba «sin
                  reacción». Apagado mientras no hay nombre, con su porqué a la
                  vista (Doctrina 2, regla 8). */}
              <button type="submit" disabled={!nuevaCarpeta.trim()}
                      aria-describedby={!nuevaCarpeta.trim() ? 'motivo-carpeta-nueva' : undefined}
                      className="bg-[var(--color-marca)] text-white px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-60">Crear</button>
              <button type="button" onClick={() => setNuevaCarpeta(null)} className="border border-gray-300 px-3 py-2 rounded-lg text-sm">Cancelar</button>
            </form>
          )}
          {nuevaCarpeta !== null && (
            <Motivo id="motivo-carpeta-nueva" cuando={!nuevaCarpeta.trim()}>
              Escribe el nombre de la carpeta para poder crearla.
            </Motivo>
          )}
          {subiendo && (
            <SubirConSoltados idCarpeta={idCarpeta} tiposPermitidos={datos?.tipos_permitidos} topeMb={datos?.tope_por_archivo_mb}
                              onTerminado={() => cargar()} onCerrar={() => setSubiendo(false)} />
          )}

          {aviso && (typeof aviso === 'string'
            ? <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded p-2">{aviso}</div>
            : <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{aviso.error}</div>)}

          {vista === 'archivos' && !hayFiltro && (
            <nav className="text-sm text-gray-600 flex flex-wrap items-center gap-1" aria-label="Dónde estás">
              <button type="button" onClick={() => setIdCarpeta(null)} className="hover:underline" onDragOver={(e) => e.preventDefault()} onDrop={(e) => alSoltar(e, null)}>Archivos</button>
              {migas.map((m) => (
                <span key={m.id} className="flex items-center gap-1">
                  <span aria-hidden="true">›</span>
                  <button type="button" onClick={() => setIdCarpeta(m.id)} className="hover:underline" onDragOver={(e) => e.preventDefault()} onDrop={(e) => alSoltar(e, m.id)}>{m.nombre}</button>
                </span>
              ))}
              {datos?.carpeta?.solo_lectura && <span className="ml-2 text-xs px-2 py-0.5 rounded bg-gray-100">Solo lectura</span>}
            </nav>
          )}

          {seleccion.length > 0 && (
            <div className="flex flex-wrap gap-2 items-center text-sm bg-gray-50 border border-gray-200 rounded-lg p-2">
              <span>{seleccion.length} seleccionado(s)</span>
              <button type="button" disabled={Boolean(porQueNo({ permisos, accion: 'papelera', seleccion }))} title={porQueNo({ permisos, accion: 'papelera', seleccion }) || 'Supr'}
                      onClick={() => hacer(() => aPapelera(seleccion))} className="border border-gray-300 px-2 py-1 rounded flex items-center gap-1 disabled:opacity-60"><Trash2 className="h-4 w-4" /> A la papelera</button>
              <button type="button" onClick={() => setSeleccion([])} className="text-gray-600">Quitar selección</button>
            </div>
          )}

          {error && <ErrorConReintento que="No se pudo cargar Archivos" porque={error} onReintentar={cargar} />}
          {!error && cargando && !datos && <Cargando filas={5} etiqueta="Cargando archivos" />}
          {!error && datos && (
            <div className={`rounded-2xl border ${arrastrando ? 'border-[var(--color-marca)] border-dashed' : 'border-transparent'}`}>
              {hayFiltro && resultados && lista.length === 0 && <SinCoincidencias que="Ningún archivo coincide con lo que buscaste" pista="Prueba con otra palabra: se busca en el nombre y en lo que dice el documento." onLimpiar={() => setBusqueda('')} />}
              {!hayFiltro && carpetas.length === 0 && lista.length === 0 && (
                vista === 'papelera'
                  ? <EstadoVacio icono={Trash2} titulo="La papelera está vacía" descripcion="Lo que mandes a la papelera se puede restaurar durante 30 días." />
                  : vista === 'compartido'
                    ? <EstadoVacio icono={Users} titulo="Nadie te ha compartido nada todavía" descripcion="Cuando alguien comparta contigo un archivo o una carpeta, aparece aquí y te llega un aviso." />
                    : vista === 'recientes'
                      ? <EstadoVacio icono={Clock} titulo="Todavía no has abierto ningún archivo" descripcion="Lo que subas o abras queda aquí para volver rápido." />
                      : vista === 'favoritos'
                        ? <EstadoVacio icono={Star} titulo="Sin favoritos" descripcion="Marca con la estrella lo que uses seguido y aparecerá aquí." />
                        : <EstadoVacio icono={Folder} titulo="Esta carpeta está vacía" descripcion={puedeSubir ? 'Sube un archivo, crea una carpeta o arrastra algo aquí.' : 'Todavía nadie ha subido nada aquí.'}
                                       accion={puedeSubir ? <button type="button" onClick={() => setSubiendo(true)} className="bg-[var(--color-marca)] text-white font-semibold px-4 py-2.5 rounded-lg text-sm flex items-center gap-2"><Upload className="h-4 w-4" /> Subir archivos</button> : undefined} />
              )}
              {(carpetas.length > 0 || lista.length > 0) && (modo === 'lista' ? (
                <ul className="divide-y divide-gray-100" aria-label="Contenido">
                  {!hayFiltro && carpetas.map((c) => (
                    <li key={`c${c.id}`} draggable onDragStart={(e) => iniciarArrastre(e, c, true)} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.stopPropagation(); alSoltar(e, c.id); }}
                        className={`flex items-center gap-3 px-2 py-2.5 cursor-pointer ${seleccionado(c, true) ? 'bg-[var(--color-marca-suave)]' : 'hover:bg-gray-50'}`}
                        onClick={(e) => alternar(c, true, e)}>
                      {c.candado ? <FolderLock className="h-5 w-5 text-amber-700 shrink-0" aria-label="Carpeta con candado" /> : <Folder className="h-5 w-5 text-[var(--color-marca)] shrink-0" aria-hidden="true" />}
                      <span className="flex-1 min-w-0 truncate text-sm text-gray-900">{c.nombre}{c.tipo === 'EXPEDIENTE' && <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Expediente</span>}</span>
                      <span className="text-xs text-gray-500 hidden sm:block">{c.candado ? CLASIFICACION[c.clasificacion]?.texto : ''}</span>
                      {vista === 'papelera' && puede('archivos.borrar') && <button type="button" className="text-xs text-[var(--color-marca)] font-semibold" onClick={(e) => { e.stopPropagation(); hacer(() => api.post(`/archivos/carpetas/${c.id}/restaurar`, {})); }}>Restaurar</button>}
                    </li>
                  ))}
                  {lista.map((a) => (
                    <li key={`a${a.id}`} draggable={vista === 'archivos'} onDragStart={(e) => iniciarArrastre(e, a, false)}
                        className={`flex items-center gap-3 px-2 py-2.5 cursor-pointer ${seleccionado(a, false) ? 'bg-[var(--color-marca-suave)]' : 'hover:bg-gray-50'}`}
                        onClick={(e) => alternar(a, false, e)}>
                      <IconoDe tipo={a.tipo} className="h-5 w-5 text-gray-500 shrink-0" />
                      <span className="flex-1 min-w-0">
                        <span className="block truncate text-sm text-gray-900">{a.nombre}</span>
                        {(hayFiltro || vista !== 'archivos') && a.migas && <span className="block text-[11px] text-gray-500 truncate">{['Archivos', ...a.migas.map((m) => m.nombre)].join(' › ')}</span>}
                      </span>
                      {a.clasificacion !== 'INTERNO' && <Lock className="h-4 w-4 text-amber-700 shrink-0" aria-label={CLASIFICACION[a.clasificacion]?.texto} />}
                      {a.favorito && <Star className="h-4 w-4 fill-amber-400 text-amber-500 shrink-0" aria-label="Favorito" />}
                      <span className="text-xs text-gray-500 hidden sm:block w-16 text-right">{tamano(a.bytes)}</span>
                      <span className="text-xs text-gray-500 hidden md:block w-24 text-right">{cuando(a.visto_en || a.actualizado_en)}</span>
                      {vista === 'papelera' && puede('archivos.borrar') && <button type="button" className="text-xs text-[var(--color-marca)] font-semibold" onClick={(e) => { e.stopPropagation(); hacer(() => api.post(`/archivos/${a.id}/restaurar`, {})); }}>Restaurar</button>}
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" aria-label="Contenido">
                  {!hayFiltro && carpetas.map((c) => (
                    <li key={`c${c.id}`} draggable onDragStart={(e) => iniciarArrastre(e, c, true)} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.stopPropagation(); alSoltar(e, c.id); }}
                        className={`border rounded-xl p-3 cursor-pointer flex flex-col items-center gap-1 text-center ${seleccionado(c, true) ? 'border-[var(--color-marca)] bg-[var(--color-marca-suave)]' : 'border-gray-200 hover:bg-gray-50'}`}
                        onClick={(e) => alternar(c, true, e)}>
                      {c.candado ? <FolderLock className="h-9 w-9 text-amber-700" aria-label="Carpeta con candado" /> : <Folder className="h-9 w-9 text-[var(--color-marca)]" aria-hidden="true" />}
                      <span className="text-sm text-gray-900 break-words w-full">{c.nombre}</span>
                    </li>
                  ))}
                  {lista.map((a) => (
                    <li key={`a${a.id}`} draggable={vista === 'archivos'} onDragStart={(e) => iniciarArrastre(e, a, false)}
                        className={`border rounded-xl p-3 cursor-pointer flex flex-col items-center gap-1 text-center ${seleccionado(a, false) ? 'border-[var(--color-marca)] bg-[var(--color-marca-suave)]' : 'border-gray-200 hover:bg-gray-50'}`}
                        onClick={(e) => alternar(a, false, e)}>
                      <IconoDe tipo={a.tipo} className="h-9 w-9 text-gray-500" />
                      <span className="text-sm text-gray-900 break-words w-full">{a.nombre}</span>
                      <span className="text-[11px] text-gray-500">{tamano(a.bytes)}{a.clasificacion !== 'INTERNO' ? ' · 🔒' : ''}</span>
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          )}
        </main>

        {fichaId && (
          <div className="md:w-96 shrink-0">
            <Ficha id={fichaId} onCerrar={() => setFichaId(null)} onCambio={() => cargar()} />
          </div>
        )}
      </div>
      {vista === 'archivos' && (
        <p className="text-[11px] text-gray-500 flex items-center gap-1"><Share2 className="h-3 w-3" aria-hidden="true" /> Atajos: N nueva carpeta · U subir · / buscar · Supr a la papelera · Ctrl+clic para seleccionar varios · arrastra para mover.</p>
      )}
    </div>
  );
}

/** El panel de subida que además recibe lo que se soltó sobre la pantalla. */
function SubirConSoltados(props) {
  const [pendientes, setPendientes] = useState(null);
  useEffect(() => {
    function alSoltar(ev) { setPendientes(ev.detail); }
    window.addEventListener('tego-archivos-soltados', alSoltar);
    return () => window.removeEventListener('tego-archivos-soltados', alSoltar);
  }, []);
  return <Subir {...props} soltados={pendientes} />;
}
